import Model from '@ember-data/model';

import { attr, belongsTo, hasMany } from '@ember-data/model';
import { cached } from '@glimmer/tracking';
import { load } from 'ember-async-data';

import filterEmptyModelRuns from 'rise-ui/utils/filter-empty-model-runs.js';
import { ITEM_STRUCTURE_TYPE_IDS, itemTypeIdsEqual } from 'rise-ui/utils/model/item.js';

const matchFileExtension = /(?:\.([^.]+))?$/;

export const ICON_TYPES = {
  IMAGE: 'image',
  MAP: 'map',
  PDF: 'pdf',
  TIME_SERIES: 'ts',
  UNKNOWN: 'unknown',
  VIDEO: 'video',
  XML: 'xml',
};

export default class ItemModel extends Model {
  @attr('string') typeId;
  @attr('string') type;
  @attr() itemType;
  @attr('string') title;
  @attr('string') description;
  @attr('boolean') isModeled;
  @attr('boolean') hasProfile;
  get isActuallyModeled() {
    return this.isModeled && !this.hasProfile
  }
  @attr('date') updateDate;
  @attr('date') publicationFirstPublicationDate;
  @attr() publicationPublisher;
  @attr() spatialGeometry;
  @attr() spatialResolution;
  @attr() spatialTransformation;
  @attr() spatialOpenDataURL;
  @attr() parameterId;
  @attr('date') temporalStartDate;
  @attr('date') temporalEndDate;
  @attr() updateFrequency;
  @attr() disclaimer;
  @attr('string') binaryFilePath;
  @attr('string') verticalDatum;

  @belongsTo('catalog-record', { async: true, inverse: 'items' }) catalogRecord;

  @hasMany('model-run', { async: true, inverse: 'item' }) modelRuns;
  @cached get asyncModelRuns() {
    return load(this.modelRuns);
  }
  @cached get validModelRuns() {
    return this.asyncModelRuns.isPending ? []
      : filterEmptyModelRuns(this.asyncModelRuns.value);
  }

  @attr() parameterName;
  @attr() parameterGroup;
  @attr() parameterUnit;
  @attr() parameterTimestep;
  @attr() parameterTransformation;
  @cached get parameter() {
    const { parameterId, parameterName, parameterGroup, parameterUnit, parameterTimestep, parameterTransformation } = this;
    return parameterId ? { parameterName, parameterGroup, parameterUnit, parameterTimestep, parameterTransformation } : null;
  }

  @cached get isTimeSeries() {
    return itemTypeIdsEqual(this.typeId, ITEM_STRUCTURE_TYPE_IDS.TIME_SERIES);
  }
  @cached get isGeospatial() {
    return itemTypeIdsEqual(this.typeId, ITEM_STRUCTURE_TYPE_IDS.GEOSPATIAL);
  }
  @cached get isBinary() {
    return itemTypeIdsEqual(this.typeId, ITEM_STRUCTURE_TYPE_IDS.BINARY);
  }
  @cached get fileFormat() {
    const ext = matchFileExtension.exec(this.binaryFilePath)[1];
    return ext && ext.toUpperCase();
  }
  @cached get icon() {
    const itemTypeId = (this.itemType?.id || '').toLowerCase();
    const fileFormat = (this.fileFormat || '').toLowerCase();
    return itemTypeId === 'art'
        ? ICON_TYPES.IMAGE
      : this.isGeospatial
        ? ICON_TYPES.MAP
      : fileFormat === 'pdf'
        ? ICON_TYPES.PDF
      : this.isTimeSeries
        ? ICON_TYPES.TIME_SERIES
      : itemTypeId === 'video'
        ? ICON_TYPES.VIDEO
      : fileFormat === 'xml'
        ? ICON_TYPES.XML
        : ICON_TYPES.UNKNOWN;
  }

  @cached get hasAllModelRunDates() {
    return this.isModeled && (
      !this.modelRuns?.every
      || this.modelRuns
        .every(mr => mr.temporalStartDate && mr.temporalEndDate)
    );
  }
}
